// Alpha authentication API client.

import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Types
export interface SignUpRequest {
  email: string;
  password: string;
  interests?: {
    trading_experience?: string;
    primary_style?: string[];
    asset_classes?: string[];
    account_size_range?: string;
    current_tools?: string[];
    automation_interest?: string[];
    referral_source?: string;
    most_wanted_feature?: string;
  };
}

export interface SignUpResponse {
  account_id: string;
  email: string;
  message: string;
  access_token: string;
  token_type: string;
  role?: string;
  email_verified: boolean;
  email_verified_at?: string | null;
  verification_required_for_trusted_feedback: boolean;
  verification_email_sent: boolean;
  dev_verification_url?: string | null;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  account_id: string;
  email: string;
  role: string;
  access_token: string;
  token_type: string;
  email_verified: boolean;
  email_verified_at?: string | null;
  verification_required_for_trusted_feedback: boolean;
  last_login_at?: string;
}

export interface AccountInfoResponse {
  account_id: string;
  email: string;
  role: string;
  email_verified: boolean;
  email_verified_at?: string | null;
  verification_required_for_trusted_feedback: boolean;
  created_at: string;
  last_login_at?: string;
  interests?: Record<string, unknown>;
}

export interface EmailVerificationResendResponse {
  email: string;
  email_verified: boolean;
  verification_email_sent: boolean;
  expires_at?: string | null;
  dev_verification_url?: string | null;
  message: string;
}

export interface EmailVerificationVerifyResponse {
  account_id: string;
  email: string;
  email_verified: boolean;
  email_verified_at?: string | null;
  verification_required_for_trusted_feedback: boolean;
  message: string;
}

export interface FeedbackRequest {
  rating: number;
  category: string;
  comment?: string;
  page_url?: string;
}

export interface FeedbackResponse {
  feedback_id: string;
  message: string;
}

export interface InterestOptionsResponse {
  trading_experience: string[];
  primary_style: string[];
  asset_classes: string[];
  account_size_range: string[];
  current_tools: string[];
  automation_interest: string[];
  referral_source: string[];
  feedback_categories: string[];
}

// Error class
export class AuthError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: unknown
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

// Request helper with auth
async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const token = useAuthStore.getState().token;
  
  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  const config: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
  
  const response = await fetch(url, config);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new AuthError(
      response.status,
      errorData.detail || `HTTP error! status: ${response.status}`,
      errorData
    );
  }
  
  return response.json();
}

// Auth API
export const authApi = {
  signUp: async (data: SignUpRequest): Promise<SignUpResponse> => {
    const response = await authRequest<SignUpResponse>('/api/alpha/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    // Store token and user info
    useAuthStore.getState().setToken(response.access_token);
    useAuthStore.getState().setUser({
      accountId: response.account_id,
      email: response.email,
      role: response.role || 'alpha',
      createdAt: new Date().toISOString(),
      emailVerified: response.email_verified,
      emailVerifiedAt: response.email_verified_at ?? undefined,
      verificationRequiredForTrustedFeedback: response.verification_required_for_trusted_feedback,
    });
    
    return response;
  },
  
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await authRequest<LoginResponse>('/api/alpha/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    // Store token and user info
    useAuthStore.getState().setToken(response.access_token);
    useAuthStore.getState().setUser({
      accountId: response.account_id,
      email: response.email,
      role: response.role || 'alpha',
      createdAt: new Date().toISOString(),
      lastLoginAt: response.last_login_at,
      emailVerified: response.email_verified,
      emailVerifiedAt: response.email_verified_at ?? undefined,
      verificationRequiredForTrustedFeedback: response.verification_required_for_trusted_feedback,
    });
    
    return response;
  },
  
  logout: async (): Promise<void> => {
    try {
      await authRequest('/api/alpha/logout', { method: 'POST' });
    } catch {
      // Ignore errors on logout
    } finally {
      useAuthStore.getState().logout();
    }
  },
  
  getAccountInfo: async (): Promise<AccountInfoResponse> => {
    return authRequest<AccountInfoResponse>('/api/alpha/me');
  },

  resendEmailVerification: async (): Promise<EmailVerificationResendResponse> => {
    const response = await authRequest<EmailVerificationResendResponse>('/api/alpha/email-verification/resend', {
      method: 'POST',
    });
    const currentUser = useAuthStore.getState().user;
    if (currentUser) {
      useAuthStore.getState().setUser({
        ...currentUser,
        emailVerified: response.email_verified,
        verificationRequiredForTrustedFeedback: !response.email_verified,
      });
    }
    return response;
  },

  verifyEmail: async (token: string): Promise<EmailVerificationVerifyResponse> => {
    const response = await authRequest<EmailVerificationVerifyResponse>('/api/alpha/email-verification/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    const currentUser = useAuthStore.getState().user;
    if (currentUser && currentUser.email.toLowerCase() === response.email.toLowerCase()) {
      useAuthStore.getState().setUser({
        ...currentUser,
        emailVerified: response.email_verified,
        emailVerifiedAt: response.email_verified_at ?? undefined,
        verificationRequiredForTrustedFeedback: response.verification_required_for_trusted_feedback,
      });
    }
    return response;
  },
  
  validateToken: async (): Promise<boolean> => {
    const token = useAuthStore.getState().token;
    if (!token) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/api/alpha/validate`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        return false;
      }

      const data = await response.json().catch(() => ({} as { valid?: boolean }));
      if (!response.ok) {
        // Preserve the current session on transient backend failures. Only
        // explicit auth failures should log the operator out.
        return true;
      }
      return data.valid !== false;
    } catch {
      // Network loss should not silently drop the operator session.
      return true;
    }
  },
  
  submitFeedback: async (data: FeedbackRequest): Promise<FeedbackResponse> => {
    return authRequest<FeedbackResponse>('/api/alpha/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  getInterestOptions: async (): Promise<InterestOptionsResponse> => {
    const url = `${API_BASE_URL}/api/alpha/interest-options`;
    const response = await fetch(url);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new AuthError(
        response.status,
        (data as { detail?: string; message?: string }).detail ??
          (data as { detail?: string; message?: string }).message ??
          `Failed to load interest options (${response.status})`,
        data,
      );
    }
    return data as InterestOptionsResponse;
  },
  
  updateInterests: async (interests: Record<string, unknown>): Promise<{ message: string; interests: Record<string, unknown> }> => {
    return authRequest('/api/alpha/interests', {
      method: 'PUT',
      body: JSON.stringify(interests),
    });
  },
};

export default authApi;
